/**
 * voxelAngler — vendored deep-sea anglerfish generator (Melanocetus johnsonii).
 *
 * Kept as close to upstream as possible: the only changes are ESM exports and the removal of
 * the standalone page's scene, UI and post pipeline. Types live in the hand-written .d.ts so
 * `allowJs` never has to be turned on.
 *
 * THE ANIMAL IS A LIGHTING PROBLEM, not a modelling one. It is ultra-black — real ones are
 * among the least reflective animals known — and the esca is the only light on it. So the lure
 * cannot just be a bright voxel: it has to illuminate the body, the teeth and the seabed, or it
 * reads as a sticker on a silhouette. The generator's job here is only to say which voxels are
 * the lure and how hard each one burns; the lighting is in AnglerMaterial and, for the rest of
 * the world, in render/LureLight.
 *
 * The illicium is a circular arc integrated in closed form — the same trick the turtle's
 * flipper uses — so the rod bends without stepping and its tip has an exact expression the CPU
 * can reproduce to place the light.
 */
const TAU = Math.PI * 2;
const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
const lerp = (a, b, t) => a + (b - a) * t;
function sstep(e0, e1, x) { const t = clamp((x - e0) / (e1 - e0), 0, 1); return t * t * (3 - 2 * t); }

function mulberry32(a) {
  return function () {
    a |= 0; a = a + 0x6D2B79F5 | 0;
    let t = Math.imul(a ^ a >>> 15, 1 | a);
    t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}

function makeNoise(seed) {
  const r = mulberry32(seed >>> 0), P = new Float32Array(1024);
  for (let i = 0; i < 1024; i++) P[i] = r();
  function h(x, y, z) {
    let n = (x * 374761393 + y * 668265263 + z * 1442695041) | 0;
    n = (n ^ (n >> 13)) * 1274126177 | 0;
    return P[(n >>> 0) & 1023];
  }
  return function (x, y, z) {
    const xi = Math.floor(x), yi = Math.floor(y), zi = Math.floor(z);
    const xf = x - xi, yf = y - yi, zf = z - zi;
    const u = xf * xf * (3 - 2 * xf), v = yf * yf * (3 - 2 * yf), w = zf * zf * (3 - 2 * zf);
    const a = lerp(h(xi, yi, zi), h(xi + 1, yi, zi), u), b = lerp(h(xi, yi + 1, zi), h(xi + 1, yi + 1, zi), u);
    const c = lerp(h(xi, yi, zi + 1), h(xi + 1, yi, zi + 1), u), d = lerp(h(xi, yi + 1, zi + 1), h(xi + 1, yi + 1, zi + 1), u);
    return lerp(lerp(a, b, v), lerp(c, d, v), w);
  };
}

function fbm(nz, x, y, z, oct, gain) {
  let a = 1, f = 1, s = 0, n = 0; gain = gain || 0.5;
  for (let i = 0; i < oct; i++) { s += a * (nz(x * f, y * f, z * f) - 0.5) * 2; n += a; a *= gain; f *= 2.03; }
  return s / n;
}

function tab(v, t) {
  const n = v.length - 1, x = clamp(t, 0, 1) * n, i = Math.min(n - 1, Math.floor(x)), f = x - i;
  return lerp(v[i], v[i + 1], f * f * (3 - 2 * f));
}
const mixc = (a, b, t) => [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)];

/* parts drive the deform; roles drive the pigment */
export const P_BODY = 0, P_JAW = 1, P_ROD = 2;
export const R_SKIN = 0, R_MOUTH = 1, R_TOOTH = 2, R_EYE = 3, R_ROD = 4, R_ESCA = 5, R_FIN = 6;

export function buildAngler(st) {
  const t0 = (typeof performance !== 'undefined' ? performance.now() : 0);
  const vox = st.vox, seed = st.seed, girth = st.girth, teethK = st.teeth;
  const rnd = mulberry32(seed >>> 0), nz = makeNoise(seed * 7919 + 11);

  const px = [], py = [], pz = [], part = [], ps = [], pu = [], pv = [], pw = [], role = [], glow = [],
    cr = [], cg = [], cbl = [], shd = [];
  let nBody = 0, nJaw = 0, nRod = 0, nTooth = 0, nEsca = 0;

  /* ---- body: a globular head that is most of the animal --------------- */
  const ZB = -0.52, ZF = 0.42;
  const HH = [0.045, 0.105, 0.205, 0.300, 0.300, 0.240, 0.085];
  const WW = [0.032, 0.080, 0.165, 0.250, 0.250, 0.195, 0.070];
  const prof = (z) => {
    const t = (z - ZB) / (ZF - ZB);
    return { h: tab(HH, t) * girth, w: tab(WW, t) * girth };
  };
  /* the gape line: hinged well back, rising to the snout */
  const gape = (z) => lerp(-0.045, 0.030, clamp((z - 0.14) / 0.28, 0, 1));
  const HINGE = [0, -0.045, 0.145];

  const inside = (x, y, z) => {
    if (z < ZB || z > ZF) return false;
    const p = prof(z);
    if (p.h < 1e-4 || p.w < 1e-4) return false;
    const lump = 1 + 0.10 * fbm(nz, x * 4.0, y * 4.0, z * 4.0, 2);
    return (x * x) / (p.w * p.w) + (y * y) / (p.h * p.h) <= lump * lump;
  };
  /* the mouth cavity: a wedge hollowed out forward of the hinge */
  const inMouth = (x, y, z) => {
    if (z < 0.10) return false;
    const p = prof(z), open = 0.30 * sstep(0.10, 0.40, z);
    return Math.abs(y - gape(z)) < open * 0.5 && Math.abs(x) < p.w * 0.86;
  };

  const SKIN = [0.028, 0.030, 0.038], SKIN2 = [0.062, 0.068, 0.082];
  for (let z = ZB - vox; z <= ZF + vox; z += vox)
    for (let x = -0.34; x <= 0.34; x += vox)
      for (let y = -0.36; y <= 0.36; y += vox) {
        if (!inside(x, y, z)) continue;
        if (inside(x + vox, y, z) && inside(x - vox, y, z) && inside(x, y + vox, z) &&
          inside(x, y - vox, z) && inside(x, y, z + vox) && inside(x, y, z - vox)) continue;
        let R = R_SKIN, pt = P_BODY;
        /* Ultra-black skin. The point is that nothing but the lure is visible until the lure
           lights it. */
        let c = mixc(SKIN, SKIN2, sstep(-0.3, 0.7, fbm(nz, x * 7, y * 7, z * 7, 3)));
        if (inMouth(x, y, z)) { R = R_MOUTH; c = [0.075, 0.042, 0.048]; }
        /* the lower jaw swings on the hinge, so it is a separate part */
        if (z > HINGE[2] && y < gape(z)) pt = P_JAW;
        for (const s of [1, -1]) {
          if (Math.hypot((x - s * 0.145) / 1.05, (y - 0.115) / 1.0, (z - 0.275) / 1.05) < 0.030) {
            R = R_EYE; c = [0.020, 0.024, 0.030];
          }
        }
        px.push(x); py.push(y); pz.push(z);
        part.push(pt); ps.push(0); pu.push(0); pv.push(0); pw.push(0); role.push(R); glow.push(0);
        cr.push(c[0]); cg.push(c[1]); cbl.push(c[2]);
        shd.push(0.88 + 0.24 * nz(x * 21, y * 21, z * 21));
        if (pt === P_JAW) nJaw++; else nBody++;
      }

  /* ---- teeth: long inward needles on both jaws ------------------------ */
  if (teethK > 0.02) {
    const NT = 9;
    for (let i = 0; i < NT; i++) {
      const f = i / (NT - 1), z = lerp(0.175, 0.395, f);
      const p = prof(z), gy = gape(z);
      const len = (0.075 + 0.045 * Math.sin(f * Math.PI)) * teethK * (0.75 + 0.5 * rnd());
      for (const s of [1, -1]) {
        const bx = s * p.w * 0.70 * (1 - 0.25 * f);
        for (const up of [1, -1]) {
          const steps = Math.max(2, Math.round(len / vox));
          for (let k = 0; k <= steps; k++) {
            const t = k / steps, rr = Math.max(vox * 0.55, (0.016 - 0.012 * t) * teethK);
            const tx = bx * (1 - 0.10 * t), ty = gy - up * len * t * 0.92 + up * 0.010,
              tz = z + 0.030 * t * (up > 0 ? 0.4 : -0.2);
            const NC = Math.max(0, Math.round(rr / vox));
            for (let a = -NC; a <= NC; a++) for (let b = -NC; b <= NC; b++) {
              if (Math.hypot(a, b) * vox > rr) continue;
              px.push(tx + a * vox); py.push(ty); pz.push(tz + b * vox);
              part.push(up < 0 ? P_JAW : P_BODY); ps.push(0); pu.push(0); pv.push(0); pw.push(0);
              role.push(R_TOOTH); glow.push(0);
              cr.push(0.74); cg.push(0.73); cbl.push(0.70);
              shd.push(0.94 + 0.12 * rnd());
              nTooth++;
            }
          }
        }
      }
    }
  }

  /* ---- caudal and pectoral fins --------------------------------------- */
  (function () {
    const NR = Math.max(4, Math.round(0.15 / vox));
    for (let i = 0; i <= NR; i++) {
      const t = i / NR, hgt = 0.055 * Math.sin(Math.pow(t, 0.6) * Math.PI) + prof(ZB).h * 0.95;
      const NY = Math.max(1, Math.round(hgt * 2 / vox));
      for (let j = 0; j <= NY; j++) {
        const y = (j / NY - 0.5) * 2 * hgt;
        if (Math.abs(y) > hgt) continue;
        px.push(0); py.push(y); pz.push(ZB - t * 0.20);
        part.push(P_BODY); ps.push(0); pu.push(0); pv.push(0); pw.push(0); role.push(R_FIN); glow.push(0);
        cr.push(0.038); cg.push(0.042); cbl.push(0.052);
        shd.push(0.90 + 0.18 * rnd());
        nBody++;
      }
    }
    for (const s of [1, -1]) {
      for (let i = 0; i <= 6; i++) {
        const t = i / 6, L = 0.10 * Math.sin(Math.pow(t, 0.7) * Math.PI) + 0.02;
        const NY = Math.max(1, Math.round(L / vox));
        for (let j = 0; j <= NY; j++) {
          px.push(s * (0.20 + t * 0.09)); py.push(-0.075 - j * vox * 0.7); pz.push(-0.055 - t * 0.05);
          part.push(P_BODY); ps.push(0); pu.push(0); pv.push(0); pw.push(0); role.push(R_FIN); glow.push(0);
          cr.push(0.038); cg.push(0.042); cbl.push(0.052);
          shd.push(0.90 + 0.18 * rnd());
          nBody++;
        }
      }
    }
  })();

  /* ---- illicium and esca ---------------------------------------------- */
  const ROD_ROOT = [0, prof(0.285).h * 0.92, 0.285];
  {
    const NS = Math.max(10, Math.round(1.0 / vox));
    for (let i = 0; i <= NS; i++) {
      const t = i / NS;
      const rr = Math.max(vox * 0.55, 0.011 * (1 - 0.35 * t));
      const NC = Math.max(0, Math.round(rr / vox));
      for (let a = -NC; a <= NC; a++) for (let b = -NC; b <= NC; b++) {
        if (Math.hypot(a, b) * vox > rr) continue;
        px.push(ROD_ROOT[0]); py.push(ROD_ROOT[1]); pz.push(ROD_ROOT[2]);
        part.push(P_ROD); ps.push(t); pu.push(a * vox); pv.push(b * vox); pw.push(0);
        role.push(R_ROD); glow.push(0);
        cr.push(0.050); cg.push(0.055); cbl.push(0.066);
        shd.push(0.92 + 0.14 * rnd());
        nRod++;
      }
    }
    const ER = 0.048;
    const NB = Math.ceil(ER / vox);
    for (let a = -NB; a <= NB; a++) for (let b = -NB; b <= NB; b++) for (let c = -NB; c <= NB; c++) {
      const d = Math.hypot(a, b, c) * vox;
      if (d > ER) continue;
      px.push(ROD_ROOT[0]); py.push(ROD_ROOT[1]); pz.push(ROD_ROOT[2]);
      part.push(P_ROD); ps.push(1.0); pu.push(a * vox); pv.push(b * vox); pw.push(c * vox);
      role.push(R_ESCA); glow.push(1 - 0.55 * (d / ER));
      cr.push(0.80); cg.push(0.98); cbl.push(0.94);
      shd.push(1.0);
      nEsca++;
    }
    const NF = 5 + (rnd() * 4 | 0);
    for (let f = 0; f < NF; f++) {
      const a0 = rnd() * TAU, L = 0.05 + rnd() * 0.07, NN = Math.max(2, Math.round(L / vox));
      for (let k = 1; k <= NN; k++) {
        const t = k / NN;
        px.push(ROD_ROOT[0]); py.push(ROD_ROOT[1]); pz.push(ROD_ROOT[2]);
        part.push(P_ROD); ps.push(1.0);
        pu.push(Math.cos(a0) * ER * 0.8 * t * 1.6); pv.push(Math.sin(a0) * ER * 0.8 * t * 1.6);
        pw.push(t * L * 0.9);
        role.push(R_ESCA); glow.push(0.55 * (1 - t * 0.7));
        cr.push(0.72); cg.push(0.95); cbl.push(0.92);
        shd.push(1.0);
        nEsca++;
      }
    }
  }

  const n = px.length;
  return {
    n, nBody, nJaw, nRod, nTooth, nEsca, vox, hinge: HINGE, rodRoot: ROD_ROOT,
    x: new Float32Array(px), y: new Float32Array(py), z: new Float32Array(pz),
    part: new Float32Array(part), s: new Float32Array(ps),
    u: new Float32Array(pu), v: new Float32Array(pv), w: new Float32Array(pw),
    role: new Float32Array(role), glow: new Float32Array(glow), shade: new Float32Array(shd),
    cr: new Float32Array(cr), cg: new Float32Array(cg), cbl: new Float32Array(cbl),
    ms: (typeof performance !== 'undefined' ? performance.now() : 0) - t0,
  };
}

/**
 * The illicium's arc, for the vertex stage.
 *
 * The rod is baked in arc-PARAMETER space and integrated here, so it bends smoothly rather than
 * stepping between segments, and its tip has an exact closed-form position the CPU reproduces
 * to place the light. Integrating a tangent of (0, sin phi, cos phi) gives
 *     vertical = (L/A)(cos p0 - cos f1)      forward = (L/A)(sin f1 - sin p0)
 * and getting those two the wrong way round sends the rod straight up instead of arcing forward
 * over the mouth, which puts the lure out of frame above the animal.
 */
export const ANGLER_ROD_GLSL = /* glsl */ `
uniform float uJaw, uRodLen, uRodArc, uRodSway, uRodPhase, uSwim, uSwimAmp;
uniform vec3 uHinge, uRodRoot;
attribute float aPart, aS, aRole, aShade, aGlow;
attribute vec3 aUV;
attribute vec3 aCol;

void anglerRod(float t, out vec3 P, out vec3 N, out vec3 B) {
  float A = -uRodArc;
  float As = (A >= 0.0 ? 1.0 : -1.0) * max(abs(A), 0.02);
  float p0 = 1.15;
  float f1 = p0 + As * t;
  float sway = uRodSway * 0.16 * sin(uRodPhase - t * 1.7);
  float lift = uRodSway * 0.05 * sin(uRodPhase * 0.7 - t * 1.2 + 1.0);
  vec3 arc = vec3(sway * t,
                  (uRodLen / As) * (cos(p0) - cos(f1)) + lift * t,
                  (uRodLen / As) * (sin(f1) - sin(p0)));
  P = uRodRoot + arc;
  vec3 T = normalize(vec3(sway * 0.6, sin(f1), cos(f1)));
  N = normalize(cross(vec3(0.0, 0.0, 1.0), T));
  if (length(cross(vec3(0.0, 0.0, 1.0), T)) < 0.05) N = vec3(1.0, 0.0, 0.0);
  B = cross(T, N);
}

/** where a voxel actually sits, once the rod, the jaw and the body wave are applied */
vec3 anglerPlace(vec3 rest) {
  if (aPart > 1.5) {
    vec3 P, N, B;
    anglerRod(clamp(aS, 0.0, 1.0), P, N, B);
    // aUV.z is the offset ALONG the rod; smuggling it through aS collapses the bulb to one
    // side of the tip instead of making it a sphere around it.
    vec3 T = normalize(cross(N, B));
    return P + N * aUV.x + B * aUV.y + T * aUV.z;
  }
  vec3 centre = rest;
  if (aPart > 0.5) {
    float a = uJaw * 1.05;
    vec3 d = rest - uHinge;
    centre = uHinge + vec3(d.x, d.y * cos(a) - d.z * sin(a), d.y * sin(a) + d.z * cos(a));
  }
  float t = clamp((0.42 - centre.z) / 0.94, 0.0, 1.0);
  centre.x += uSwimAmp * 0.055 * pow(t, 1.7) * sin(uSwim - t * 2.4);
  return centre;
}
`;

/**
 * The esca's position in MODEL space, the same arc the shader walks.
 *
 * The CPU has to reproduce it exactly, because this is where the light goes — and a light that
 * is a few centimetres off its bulb lights the animal from the wrong side.
 */
export function escaLocal(rodRoot, rodLen, rodArc, rodSway, rodPhase, out) {
  const A = -rodArc, As = (A >= 0 ? 1 : -1) * Math.max(Math.abs(A), 0.02), p0 = 1.15, t = 1.0;
  const f1 = p0 + As * t;
  const sway = rodSway * 0.16 * Math.sin(rodPhase - t * 1.7);
  const lift = rodSway * 0.05 * Math.sin(rodPhase * 0.7 - t * 1.2 + 1.0);
  out[0] = rodRoot[0] + sway * t;
  out[1] = rodRoot[1] + (rodLen / As) * (Math.cos(p0) - Math.cos(f1)) + lift * t;
  out[2] = rodRoot[2] + (rodLen / As) * (Math.sin(f1) - Math.sin(p0));
  return out;
}

export default { buildAngler, escaLocal, ANGLER_ROD_GLSL };
